import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormOptionsLists} from "../../../shared/FormOptionsLists";
import {UntypedFormControl} from "@angular/forms";
import {Observable} from "rxjs";
import {HttpParams} from "@angular/common/http";
import {debounceTime, distinctUntilChanged, map, startWith} from "rxjs/operators";

@Component({
    selector: 'app-search-bar',
    templateUrl: './search-bar.component.html',
    styleUrl: './search-bar.component.scss',
    standalone: false
})
export class SearchBarComponent implements OnInit, OnChanges {
  @Input() showDetailedSearch!: boolean;
  // toggled by a parent to clear filters without also clearing the category (no resetFilter emit)
  @Input() resetTrigger: boolean;
  // set once by a parent (e.g. landing here from a global search "view all" link) to seed the name filter
  @Input() initialQuery: string | null = null;

  @Output() search = new EventEmitter<HttpParams>
  @Output() resetFilter = new EventEmitter<void>

  // filter options
  formOptionsLists = new FormOptionsLists();
  // filter by name
  searchControl = new UntypedFormControl('');
  searchOptionsList = this.formOptionsLists.searchOptionsList;
  searchOptionsControl = new UntypedFormControl(this.formOptionsLists.searchOptionsList[0]);
  // filter by level
  levelList = this.formOptionsLists.levelList;
  levelControl = new UntypedFormControl(this.levelList);
  // snapshot of the level selection before the most recent toggle click, so a click can
  // tell whether all levels were selected beforehand (see onLevelToggle)
  private previousLevelSelection: string[] = [...this.levelList];
  // filter by level type
  levelFilterModeList = this.formOptionsLists.levelFilterModeList;
  levelFilterModeControl = new UntypedFormControl(this.levelFilterModeList[0]);
  // filter by word type
  typeList = this.formOptionsLists.typeList;
  typeControl = new UntypedFormControl('');
  // filter by country
  countryList = this.formOptionsLists.countryList;
  countryControl = new UntypedFormControl('');
  // filter by word field
  wordFieldList = this.formOptionsLists.wordFieldList;
  wordFieldControl = new UntypedFormControl(this.formOptionsLists.wordFieldList[0]);
  // filter by test name
  testList = this.formOptionsLists.testList;
  testsControl = new UntypedFormControl('');
  filteredTests: Observable<string[]>;


  // view length
  accordion = 0;

  constructor() {
  }

  ngOnInit(){
    this.filteredTests = this.testsControl.valueChanges
      .pipe(
        startWith(''),
        map(value => this._filter(value))
      );

    // debounce free-text typing so we don't fire a request per keystroke;
    // other controls (dropdowns, toggles) still search immediately on selection
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(() => this.search.emit(this.getParams()));

    this.applyInitialQuery();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['resetTrigger'] && !changes['resetTrigger'].firstChange) {
      this.resetControls();
      this.search.emit(this.getParams())
    }
    if (changes['initialQuery'] && !changes['initialQuery'].firstChange) {
      this.applyInitialQuery();
    }
  }

  private applyInitialQuery() {
    if (this.initialQuery) {
      // emitEvent: false - explicitly emit once below instead of relying on the
      // debounced valueChanges subscription, matching how resetControls() callers do it
      this.searchControl.setValue(this.initialQuery, {emitEvent: false});
      this.searchOptionsControl.setValue(this.searchOptionsList[0]);
      this.search.emit(this.getParams());
    }
  }

  /**
   * reset all filters and search with the original parameters
   */
  deleteFilter() {
    this.resetControls();
    this.resetFilter.emit()
    this.search.emit(this.getParams())
  }

  private resetControls() {
    // emitEvent: false — callers of resetControls() already trigger the search
    // explicitly right after; letting valueChanges fire too would just queue a
    // redundant debounced search on top of it
    this.searchControl.setValue('', {emitEvent: false});
    this.levelFilterModeControl.setValue(this.levelFilterModeList[0]);
    this.levelControl.setValue(this.levelList);
    this.previousLevelSelection = [...this.levelList];
    this.typeControl.setValue('');
    this.countryControl.setValue('');
    this.testsControl.setValue('');
    this.wordFieldControl.setValue(this.wordFieldList[0]);
    this.searchOptionsControl.setValue(this.searchOptionsList[0]);
  }

  /**
   * checks if the element is in the searchArray
   *
   * @param searchArray contains all levels, which are selected
   * @param element a single level
   */
  contains(searchArray: string[], element: string) {
    if (searchArray == null) {
      return false;
    }
    for (const arrayElement of searchArray) {
      if (element === arrayElement) {
        return true;
      }
    }
    return false;
  }

  /**
   * handles a click on a level toggle button: if all levels were still selected before
   * this click, isolate the selection to just the clicked level instead of leaving the
   * others selected (the default multiple-toggle behavior would just deselect the
   * clicked one). Once the selection has been narrowed down, further clicks toggle
   * normally; selecting every level again by hand restores the "all selected" state and
   * the isolating behavior re-applies on the next click.
   *
   * @param clickedLevel the value of the toggle that was clicked
   */
  onLevelToggle(clickedLevel: string) {
    if (this.previousLevelSelection.length === this.levelList.length) {
      this.levelControl.setValue([clickedLevel]);
    }
    this.previousLevelSelection = [...this.levelControl.value];
    this.search.emit(this.getParams());
  }

  /**
   * change viewSize for table grid depending on the advanced search
   */
  changeViewSize() {
    if (this.accordion === 0) {
      this.accordion = 3;
    } else {
      this.accordion = 0;
    }
  }

  /**
   * convert a String array of type values to a list of the corresponding BE values
   *
   * @param value string array to convert
   */
  typeListToString(value: string[]): string {
    let tmp = '';
    let index = 0;
    value.forEach(element => {
        index = this.formOptionsLists.typeList.indexOf(element);
        if (index > -1 && element !== value[value.length - 1]) {
          tmp = tmp + this.formOptionsLists.typeBEList[index] + ',';
        }
        if (index > -1 && element === value[value.length - 1]) {
          tmp = tmp + this.formOptionsLists.typeBEList[index];
        }
      }
    );
    return tmp;
  }

  /**
   * convert a String array of type values to a list of the corresponding BE values
   *
   * @param value string array to convert
   */
  countryListToString(value: string[]): string {
    let tmp = '';
    let index = 0;
    value.forEach(element => {
        index = this.formOptionsLists.countryList.indexOf(element);
        if (index > -1 && element !== value[value.length - 1]) {
          tmp = tmp + this.formOptionsLists.countryBEList[index] + ',';
        }
        if (index > -1 && element === value[value.length - 1]) {
          tmp = tmp + this.formOptionsLists.countryBEList[index];
        }
      }
    );
    return tmp;
  }

  /**
   * filter the values of the different tests with the given string
   *
   *  @param value user editable string
   */
  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.testList.filter(option => option.toLowerCase().includes(filterValue));
  }

  protected getParams(){
    let params = new HttpParams();

    switch (this.searchOptionsControl.value) {
      case this.searchOptionsList[0]:
        params = params.append('name', String(this.searchControl.value));
        break;
      case this.searchOptionsList[1]:
        params = (this.searchControl.value !== '') ? params.append('categoryName', String(this.searchControl.value)) : params;
        break;
      case this.searchOptionsList[2]:
        params = (this.searchControl.value !== '') ? params.append('vocabEntryOrCategoryName', String(this.searchControl.value)) : params;
        break;
    }
    switch (this.levelFilterModeControl.value) {
      case this.levelFilterModeList[0]:
        params = params.append('productive', String(this.levelControl.value));
        break;
      case this.levelFilterModeList[1]:
        params = params.append('receptive', (this.levelControl.value));
        break;
      case this.levelFilterModeList[2]:
        params = params.append('productive', String(this.levelControl.value));
        params = params.append('receptive', (this.levelControl.value));
        break;
    }

    if (this.typeControl.value !== '') {
      params = params.append('vocabTypes', this.typeListToString(this.typeControl.value));
    }
    if (this.countryControl.value !== '') {
      params = params.append('country', this.countryListToString(this.countryControl.value));
    }
    if (this.testsControl.value !== '') {
      params = params.append('vocabEntryTest', String(this.testsControl.value));
    }


    // Set field params
    switch (this.wordFieldControl.value) {
      case this.wordFieldList[1]:
        params = params.append('isWordField', 'true');
        break;
      case this.wordFieldList[2]:
        params = params.append('isDach', 'true');
        break;
      case this.wordFieldList[3]:
        params = params.append('isWordField', 'false');
        params = params.append('isDach', 'false');
        break;
    }
    return params
  }
}
